import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { SelfUser } from "@vmeste/shared";
import { ApiError, api } from "../lib/api.js";
import { syncEngine } from "../lib/sync.js";
import { forgetCodecs, forgetSpaceKeys } from "../lib/spacekeys.js";
import { forgetMediaCache, forgetQueuedUploads } from "../lib/media.js";
import { forgetReadCursors } from "./unread.js";
import { useT } from "../i18n/index.js";
import { toSpaceView } from "../lib/views.js";
import type { SpaceView } from "./views.js";

/**
 * Who is signed in, and which space they are looking at.
 *
 * The session itself lives in an httpOnly cookie (SPEC §8), so nothing here
 * holds a token and a reload simply asks the server who it is talking to. Only
 * the chosen space id is kept locally, because that is a preference rather than
 * a credential.
 */

const SPACE_KEY = "vmeste.space";

export type SessionUser = SelfUser;

interface SessionState {
  user: SessionUser;
  spaces: SpaceView[];
  spaceId: string;
}

interface Credentials {
  email: string;
  password: string;
}

interface Registration extends Credentials {
  inviteCode: string;
  displayName: string;
  /**
   * What to call the space an `app` invite code creates.
   *
   * Only read by the server for a code that names no space; a code into an
   * existing space puts the person somewhere already named. Declared here
   * because the register screen sends it — without it the field travelled to
   * the server through a type that did not admit it existed, which is exactly
   * the sort of thing the compiler is for.
   */
  spaceName?: string;
}

interface SessionContextValue {
  session: SessionState | null;
  space: SpaceView | null;
  /** True until the first `me` call settles, so the app does not flash sign-in. */
  loading: boolean;
  pending: boolean;
  error: string | null;
  signIn(input: Credentials): Promise<void>;
  register(input: Registration): Promise<void>;
  signOut(): Promise<void>;
  selectSpace(spaceId: string): void;
  refresh(): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function readPreferredSpace(): string | null {
  try {
    return localStorage.getItem(SPACE_KEY);
  } catch {
    return null;
  }
}

function writePreferredSpace(spaceId: string | null): void {
  try {
    if (spaceId) localStorage.setItem(SPACE_KEY, spaceId);
    else localStorage.removeItem(SPACE_KEY);
  } catch {
    /* storage unavailable — the choice lasts for this session only */
  }
}

function toState(
  response: { user: SessionUser; spaces: Parameters<typeof toSpaceView>[0][] },
  preferred: string | null,
): SessionState | null {
  const spaces = response.spaces.map(toSpaceView);
  if (spaces.length === 0) return null;

  const spaceId =
    preferred && spaces.some((space) => space.id === preferred)
      ? preferred
      : spaces[0]!.id;

  return { user: response.user, spaces, spaceId };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [session, setSession] = useState<SessionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const adopt = useCallback((next: SessionState | null) => {
    setSession(next);

    if (next) {
      writePreferredSpace(next.spaceId);
      syncEngine.start(
        next.user.id,
        next.spaces.map((space) => space.id),
      );
    } else {
      syncEngine.stop();
    }
  }, []);

  // Ask the server who we are. The cookie may have expired while the app sat on
  // a home screen for a month, so this is the only source of truth.
  useEffect(() => {
    const controller = new AbortController();

    api
      .me(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        adopt(toState(response, readPreferredSpace()));
      })
      .catch(() => {
        // 401 is the ordinary "not signed in" case, and an offline start cannot
        // be distinguished from it without a cached session. Either way the
        // sign-in screen is the honest thing to show.
      })
      .finally(() => {
        /**
         * Not when this request was abandoned.
         *
         * StrictMode mounts twice in development, so the first effect's cleanup
         * aborts its own request — and clearing the flag there produced a frame
         * where loading was false and the session still null. The layout read
         * that as signed out, sent the person to /signin, and the sign-in screen
         * bounced them to /lists the moment the second request landed. A deep
         * link to any screen went to the list of lists instead.
         */
        if (controller.signal.aborted) return;
        setLoading(false);
      });

    return () => controller.abort();
  }, [adopt]);

  /** Re-read who this is and what they are in. Also the answer to a space that
      has gone: the selected one is chosen from whatever comes back. */
  const refresh = useCallback(async () => {
    const response = await api.me();
    adopt(toState(response, readPreferredSpace()));
  }, [adopt]);

  // A session revoked on another device, or simply expired, must not leave the
  // app sitting on a screen it can no longer load.
  useEffect(() => {
    /*
     * The session goes; the key stays. That asymmetry is deliberate.
     *
     * Signing out is somebody saying "not on this device" — a borrowed laptop,
     * a shared computer — and it takes the key with it. A session that merely
     * ended is not that sentence: a cookie lapses after a month on a home
     * screen, a password reset deletes every session, the other person removes
     * a device. Wiping the key on each of those would demand a 28-symbol
     * recovery code on a phone as the price of an expiry, which is how a code
     * ends up written somewhere convenient, or lost.
     *
     * Nor would it buy much: a stolen phone with the app open already shows
     * everything, and reaches this branch only if it is online and its holder
     * lets it be. The key is protected by the device, which is the only thing
     * that is actually holding it.
     */
    syncEngine.onUnauthenticated = () => {
      adopt(null);
      setError(t("auth.sessionExpired"));
    };
    /*
     * A space that has gone: the other person deleted it, or removed this one
     * from it. Until this existed the app went on showing a space the server
     * refused, and only a reload put it right — so somebody could be looking at
     * lists that no longer exist and adding to them.
     *
     * `refresh` rather than anything clever: the server's list of spaces is the
     * answer, and reading it again also moves this device to another space,
     * since the selected one is chosen from what comes back.
     */
    syncEngine.onSpaceGone = () => {
      void refresh().catch(() => undefined);
    };

    return () => {
      syncEngine.onUnauthenticated = null;
      syncEngine.onSpaceGone = null;
    };
  }, [adopt, refresh, t]);

  const run = useCallback(
    async (work: () => Promise<{ user: SessionUser; spaces: never[] }>) => {
      setPending(true);
      setError(null);
      try {
        const response = await work();
        adopt(toState(response, readPreferredSpace()));
      } catch (cause) {
        // Surfaced through `error` rather than rethrown: a rejected promise from
        // a submit handler becomes an unhandled rejection and tells the person
        // nothing.
        setError(
          cause instanceof ApiError
            ? cause.message
            : t("auth.serverUnreachable"),
        );
      } finally {
        setPending(false);
      }
    },
    [adopt, t],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      space: session
        ? (session.spaces.find((s) => s.id === session.spaceId) ?? null)
        : null,
      loading,
      pending,
      error,

      signIn: (input) => run(() => api.signIn(input) as never),
      register: (input) => run(() => api.register(input) as never),

      async signOut() {
        try {
          await api.signOut();
        } finally {
          /*
           * Everything this device was keeping, not just the session.
           *
           * The keys have a comment above them saying signing out has to do
           * this, and until now nothing did: a key left in IndexedDB beside a
           * synced copy of the ciphertext is the whole encryption undone by the
           * one action somebody takes precisely because they are worried — a
           * shared computer, a borrowed laptop. The queued uploads are the same
           * argument with photographs instead of words, and the decrypted
           * files this tab is still holding are the same argument again: they
           * now outlive the screens that showed them, so somebody has to say
           * when they end.
           *
           * Before `adopt(null)`, so nothing renders against a space whose key
           * is halfway gone. Failures are swallowed: a device that cannot reach
           * its own storage still has to be able to sign out.
           */
          await forgetSpaceKeys().catch(() => undefined);
          await forgetQueuedUploads().catch(() => undefined);
          forgetMediaCache();
          forgetReadCursors();
          forgetCodecs();

          adopt(null);
          writePreferredSpace(null);
        }
      },

      selectSpace(spaceId) {
        setSession((prev) => {
          if (!prev || prev.spaceId === spaceId) return prev;
          writePreferredSpace(spaceId);
          return { ...prev, spaceId };
        });
      },

      refresh,
    }),
    [session, loading, pending, error, run, adopt, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}
